import isEqual from 'lodash/isEqual'
import React from 'react'

import type {
  FilterOperator,
  FilterOperatorOption,
  FilterPresetOption,
} from 'types/filter-page'

import { Flex, RadioGroup, Text } from 'components/ds'
import { formatComparisonOperator } from 'components/search/search-filters/utils'

type Props = {
  operator: FilterOperator
  // Needed to tell which preset, if any, the committed filter matches.
  value?: unknown
  /** Options that edit a value; keyed by their operator. */
  operators?: FilterOperatorOption[]
  /** Options committing a fixed pair; keyed by id, listed after the operators. */
  presets?: FilterPresetOption[]
  onChange: (value: any, operator: FilterOperator) => void
  // Catches key presses (e.g. Enter to apply) even for options with no input.
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>
}

export const FilterOperatorSelector = ({
  operator,
  value,
  operators = [],
  presets = [],
  onChange,
  onKeyDown,
}: Props) => {
  // A preset outranks its own operator: `equals 0` on "Appears on" shows as
  // "No playlists", not as `is` with 0 typed in.
  const selectedPreset = presets.find(
    preset => preset.operator === operator && isEqual(preset.value, value),
  )
  const selectedKey = selectedPreset?.id ?? operator

  // Presets have no input, so they commit their pair on selection.
  const handleValueChange = (key: string) => {
    const preset = presets.find(p => p.id === key)
    if (preset) {
      onChange(preset.value, preset.operator)
      return
    }
    onChange(undefined, key as FilterOperator)
  }

  const items = [
    ...operators.map(option => ({
      key: option.operator,
      label: option.label ?? formatComparisonOperator(option.operator),
      component: option.component,
    })),
    ...presets.map(preset => ({
      key: preset.id,
      label: preset.label,
      component: null,
    })),
  ]

  return (
    <RadioGroup.Root
      value={selectedKey}
      onKeyDown={onKeyDown}
      onValueChange={handleValueChange}
    >
      <Flex gap="4" direction="column">
        {items.map(item => (
          <RadioGroup.Item
            value={item.key}
            key={item.key}
            style={{
              width: '100%',
            }}
          >
            <Flex gap="2" direction="column">
              <Text>{item.label}</Text>
              {item.component && item.key === selectedKey && (
                <span>{item.component}</span>
              )}
            </Flex>
          </RadioGroup.Item>
        ))}
      </Flex>
    </RadioGroup.Root>
  )
}
